import { DoBusyTask, Errors, useDoBusyTaskEffect } from "components/DataBaseComponent";
import { ProcessContextProps, loadProcess, ProcessContext, ProcessContextLookupState, useBoundProcessActions } from "./ProcessContext";
import { ProcessPageSupportedActions, ProcessContextModelState } from "../types";
import React from "react";
import { IProcessResource, Permission, ProcessType, ProjectResource, isDeploymentProcessResource, isRunbookProcessResource } from "client/resources";
import ActionTemplateSearchResource from "client/resources/actionTemplateSearchResource";
import FeedResource from "client/resources/feedResource";
import { repository } from "clientInstance";
import { ProcessStateSelectors, getSelectors, processContextModelStateReducer, getProcessContextModelInitialState } from "./ProcessContextState";
import { isAllowed } from "components/PermissionCheck/PermissionCheck";
import { ProcessSearchFilterController } from "./ProcessSearchFilter/ProcessSearchFilterContext";
import { ProcessErrorsController } from "./ProcessErrors/ProcessErrorsContext";
import ProjectContextRepository from "client/repositories/projectContextRepository";
import { withProjectContext, WithProjectContextInjectedProps } from "areas/projects/context";

interface ProcessControllerProps {
    id: string;
    doBusyTask: DoBusyTask;
    children: (renderProps: ProcessContextProps) => React.ReactNode;
    processType: ProcessType;
    project: Readonly<ProjectResource>;
    layoutActions: ProcessPageSupportedActions;
    errors?: Errors;
}

type Props = ProcessControllerProps & WithProjectContextInjectedProps;

const useProcessState = () => {
    return React.useState<ProcessContextLookupState>({
        actionTemplates: [],
        feeds: [],
    });
};

const useLoadProcessEffect = (projectContextRepository: ProjectContextRepository, processType: ProcessType, id: string, doBusyTask: DoBusyTask, onLoaded: (process: IProcessResource) => void) => {
    return useDoBusyTaskEffect(
        doBusyTask,
        async () => {
            if (!id) {
                return;
            }

            const result: IProcessResource = await loadProcess(projectContextRepository, processType, id);

            if (onLoaded) {
                onLoaded(result);
            }
        },
        [id, processType, projectContextRepository]
    );
};

const getStateUpdaters = (setState: React.Dispatch<React.SetStateAction<ProcessContextLookupState>>) => {
    return {
        onActionTemplatesUpdated: (actionTemplates: ActionTemplateSearchResource[]) => setState(current => ({ ...current, actionTemplates })),
        onFeedsUpdated: (feeds: FeedResource[]) => setState(current => ({ ...current, feeds })),
    };
};

const useSelectors = (state: ProcessContextModelState): ProcessStateSelectors => {
    return React.useMemo(() => getSelectors(state), [state]);
};

const ProcessControllerInternal: React.FC<Props> = (props: Props) => {
    const { children, doBusyTask, id, processType, project, layoutActions } = props;
    const [state, dispatch] = React.useReducer(processContextModelStateReducer, getProcessContextModelInitialState(processType));
    const selectors = useSelectors(state);

    const [lookupsState, setState] = useProcessState();

    const boundActions = useBoundProcessActions(dispatch);

    const refreshFromServer = useLoadProcessEffect(props.projectContext.state.projectContextRepository, processType, id, doBusyTask, process => {
        boundActions.setProcess(process, true);
    });

    const stateUpdaters = React.useMemo(() => getStateUpdaters(setState), [setState]);

    const refreshActionTemplates = useDoBusyTaskEffect(
        doBusyTask,
        async () => {
            const templates = await repository.ActionTemplates.search();
            stateUpdaters.onActionTemplatesUpdated(templates);
        },
        []
    );

    const refreshFeeds = useDoBusyTaskEffect(
        doBusyTask,
        async () => {
            const feeds = isAllowed({ permission: Permission.FeedView, project: project.Id, wildcard: true }) ? await repository.Feeds.all() : [];
            stateUpdaters.onFeedsUpdated(feeds);
        },
        []
    );

    const saveOnServer = async (projectContextRepository: ProjectContextRepository, process: IProcessResource, setErrors: (errors: Errors) => void, onSuccess: () => void, commitMessage?: string): Promise<IProcessResource | null> => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let processResult: IProcessResource | null = null;
        await doBusyTask(
            async () => {
                if (isDeploymentProcessResource(process)) {
                    processResult = await projectContextRepository.DeploymentProcesses.modify(process, commitMessage);
                } else if (isRunbookProcessResource(process)) {
                    processResult = await repository.RunbookProcess.modify(process);
                }

                if (processResult && id === processResult.Id) {
                    boundActions.setProcess(processResult, true);
                }
            },
            true,
            setErrors
        );
        return processResult;
    };

    const contextValue: ProcessContextProps = {
        lookupsState,
        state,
        actions: {
            ...boundActions,
            saveOnServer,
            onActionTemplatesUpdated: stateUpdaters.onActionTemplatesUpdated,
            onFeedsUpdated: stateUpdaters.onFeedsUpdated,
            refreshFromServer,
            refreshActionTemplates,
            refreshFeeds,
            ...layoutActions,
        },
        selectors,
    };

    return (
        <ProcessContext.Provider value={contextValue}>
            <ProcessSearchFilterController processType={contextValue.state.processType} selectors={contextValue.selectors}>
                {() => {
                    return <ProcessErrorsController>{children(contextValue)}</ProcessErrorsController>;
                }}
            </ProcessSearchFilterController>
        </ProcessContext.Provider>
    );
};

export const ProcessController = withProjectContext(ProcessControllerInternal);
